import { useEffect } from 'react';
import { projectId, publicAnonKey } from '../utils/supabase/info';
import { isDevelopmentMode, getEnvironmentInfo } from '../utils/environment';
import { fontService } from './FontService';

interface FontLoaderProps {
  refreshTrigger?: number; // Used to trigger re-loading of fonts
}

export default function FontLoader({ refreshTrigger = 0 }: FontLoaderProps) {
  
  // Function to load default fonts when server is unavailable
  const loadDefaultFonts = () => {
    try {
      // Check if document is available
      if (typeof document === 'undefined') {
        return;
      }
      
      // Remove any existing font loader styles to avoid conflicts
      const existingStyles = document.querySelectorAll('style[data-font-loader]');
      existingStyles.forEach(style => style.remove());
      
      // Check if Google Fonts are already loaded
      const existingGoogleFonts = document.querySelector('link[href*="fonts.googleapis.com"]') || 
                            document.querySelector('style[data-font-loader="google-fonts"]');
      
      // Google Fonts are now loaded via index.html for better performance
      
      try {
        const defaultCSS = `
/* Default fonts - Brand fonts with fallbacks */
/* Font Face Declarations for Brand Fonts */
@font-face {
  font-family: 'Hello Paris';
  src: local('Hello Paris'), local('HelloParis'), local('Hello-Paris');
  font-weight: 400;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: 'Hello Paris';
  src: local('Hello Paris Bold'), local('HelloParis-Bold'), local('Hello-Paris-Bold');
  font-weight: 700;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: 'Sukar';
  src: local('Sukar'), local('Sukar-Regular');
  font-weight: 300;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: 'Sukar';
  src: local('Sukar'), local('Sukar-Regular');
  font-weight: 400;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: 'Sukar';
  src: local('Sukar Medium'), local('Sukar-Medium');
  font-weight: 500;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: 'Sukar';
  src: local('Sukar Bold'), local('Sukar-Bold');
  font-weight: 700;
  font-style: normal;
  font-display: swap;
}

/* CSS Variables for brand fonts */
:root {
  --font-logo: 'Hello Paris', 'Dancing Script', cursive;
  --font-body: 'Sukar', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  --font-headings: 'Sukar', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* Brand font application with enhanced specificity */
html body .font-hello-paris,
html body [data-logo="true"],
html .itinlux-logo,
html .logo,
html .site-logo,
html .brand {
  font-family: 'Hello Paris', 'Dancing Script', cursive !important;
  font-weight: 400 !important;
}

html body .font-sukar,
html body [data-body="true"],
html body [data-heading="true"],
html body p,
html body h1,
html body h2,
html body h3,
html body h4,
html body h5,
html body h6,
html body button,
html body input,
html body textarea,
html body label {
  font-family: 'Sukar', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
}

html body {
  font-family: 'Sukar', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
}

/* Force font display optimization */
* {
  font-display: swap !important;
}

/* Ensure brand fonts load properly */
.font-hello-paris {
  font-family: 'Hello Paris', 'Dancing Script', cursive !important;
}

.font-sukar {
  font-family: 'Sukar', 'Inter', sans-serif !important;
}

/* Logo specific styling with brand font */
.itinlux-logo {
  font-family: 'Hello Paris', 'Dancing Script', cursive !important;
  font-weight: 400 !important;
  font-style: normal !important;
}

/* Additional overrides for maximum compatibility */
[data-logo="true"] {
  font-family: 'Hello Paris', 'Dancing Script', cursive !important;
}

[data-body="true"],
[data-heading="true"] {
  font-family: 'Sukar', 'Inter', sans-serif !important;
}
`;
        const styleElement = document.createElement('style');
        styleElement.setAttribute('data-font-loader', 'default-fallback');
        styleElement.textContent = defaultCSS;
        
        if (document.head) {
          document.head.appendChild(styleElement);
        }
      } catch (error) {
        // Silently fail - fonts will use browser defaults
      }
    } catch (error) {
      // Silently fail - fonts will use browser defaults
    }
  };

  useEffect(() => {
    const loadFonts = async () => {
      try {
        // Use the FontService to handle font loading
        await fontService.loadBrandFonts();
        
        // If FontService fails, fall back to default loading
      } catch (error) {
        // Fallback: Use the original method
        try {
          const isDevelopment = isDevelopmentMode();
          const isValidConfig = projectId && projectId !== 'your-project-id' && publicAnonKey;
          
          if (isDevelopment || !isValidConfig) {
            loadDefaultFonts();
            return;
          }
          
          // Try to load custom fonts from server with timeout
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 3000);
          
          try {
            const response = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-a35ebf46/fonts/css`, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${publicAnonKey}`,
                'Accept': 'text/css',
              },
              signal: controller.signal,
            });
            
            clearTimeout(timeoutId);
            
            if (response.ok) {
              const css = await response.text();
              
              if (css.length > 100) {
                const styleElement = document.createElement('style');
                styleElement.setAttribute('data-font-loader', 'custom-fonts');
                styleElement.setAttribute('type', 'text/css');
                styleElement.textContent = css;
                
                if (document.head) {
                  document.head.appendChild(styleElement);
                }
              } else {
                loadDefaultFonts();
              }
            } else {
              loadDefaultFonts();
            }
          } catch (fetchError) {
            clearTimeout(timeoutId);
            loadDefaultFonts();
          }
          
        } catch (fallbackError) {
          loadDefaultFonts();
        }
      }
    };

    // Execute font loading with error handling
    loadFonts().catch(() => loadDefaultFonts());
  }, [refreshTrigger]);

  // This component doesn't render anything
  return null;
}